// Empleados (extensión de financiero.partner + ficha nomina.empleados).
// Nómina → Personal. Lista por RPC nom_empleados; alta/edición por nom_empleado_guardar.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, History, KeyRound, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, Upload, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { ImportarPersonalModal } from './ImportarPersonal';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  Campo, FRECUENCIAS_PAGO, Modal, Switch, fieldCls, money, fecha, nomRpc,
  GENEROS, ESTADOS_CIVILES, BANCOS, TIPOS_CONTRATO, PARENTESCOS,
  centrosCostoList, useDialogoEnLinea,
  type CargasData, type EmpleadoRow, type EmpleadoFicha, type CentroCostoOpt, type ContratoRow,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

// Etiqueta de jornada del contrato (mismo criterio que Contratos.tsx / PortalEmpleado.tsx).
const JORNADAS: Record<string, string> = { completa: 'Tiempo completo', parcial: 'Tiempo parcial' };
const jornadaLabel = (j: string | null | undefined) => (j ? JORNADAS[j] ?? j : '—');
const tipoContratoLabel = (t: string | null | undefined) => (t ? (TIPOS_CONTRATO[t] ?? t).split(' (')[0] : '—');

// Contrato vigente (o el más reciente) para mostrar de solo consulta en la ficha.
function contratoVigente(contratos: ContratoRow[] | undefined): ContratoRow | null {
  if (!contratos || contratos.length === 0) return null;
  return contratos.find((c) => c.estado === 'vigente')
    ?? [...contratos].sort((a, b) => (b.fecha_inicio ?? '').localeCompare(a.fecha_inicio ?? ''))[0];
}

const CARGA_VACIA = { id: '', parentesco: 'hijo', nombre: '', identificacion: '', fecha_nacimiento: '', discapacidad: false, acreditada: false, documento: '', percibe_ingresos: false };

function ChipRegimen({ etiqueta, califica, acreditada }: { etiqueta: string; califica: boolean; acreditada: boolean }) {
  const cls = califica && acreditada ? 'bg-estable-soft text-estable'
    : califica ? 'bg-pendiente-soft text-pendiente' : 'bg-muted text-muted-foreground';
  const sufijo = califica && acreditada ? '✓' : califica ? 'sin doc.' : '✗';
  return <span className={`px-1.5 py-0.5 rounded-full text-xs ${cls}`}>{etiqueta} {sufijo}</span>;
}

interface AuditCambio { campo: string; antes: unknown; despues: unknown }
interface AuditRow { fecha: string; operacion: string; tabla: string; ip: string | null; equipo: string | null; usuario: string; cambios: AuditCambio[] }
const OP_LABEL: Record<string, string> = { INSERT: 'Creación', UPDATE: 'Modificación', DELETE: 'Eliminación' };
const auditVal = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));

function HistorialModal({ empleado, cerrar }: { empleado: EmpleadoRow; cerrar: () => void }) {
  const { data = [], isFetching } = useQuery<AuditRow[]>({
    queryKey: ['nom-emp-auditoria', empleado.id],
    queryFn: () => nomRpc<AuditRow[]>('nom_empleado_auditoria', { _empleado_id: empleado.id }),
  });
  return (
    <Modal title={`Historial de cambios — ${empleado.razon_social}`} onClose={cerrar} wide>
      {isFetching ? (
        <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
      ) : data.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Sin cambios registrados para este empleado.</p>
      ) : (
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          {data.map((a, i) => (
            <div key={i} className="rounded-md border p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{OP_LABEL[a.operacion] ?? a.operacion} · {a.tabla === 'contratos' ? 'contrato' : a.tabla === 'partner' ? 'datos personales' : a.tabla === 'cargas_familiares' ? 'carga familiar' : 'empleado'}</span>
                <span className="text-xs text-muted-foreground">{new Date(a.fecha).toLocaleString(LOCALE)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Usuario: <b className="text-foreground">{a.usuario}</b>
                {a.ip && <> · IP: {a.ip}</>}
                {a.equipo && <> · <span title={a.equipo}>{a.equipo.length > 48 ? a.equipo.slice(0, 48) + '…' : a.equipo}</span></>}
              </div>
              {a.cambios.length > 0 && (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Campo</th><th className="py-1 pr-3">Antes</th><th className="py-1">Después</th></tr></thead>
                    <tbody>
                      {a.cambios.map((c, j) => (
                        <tr key={j} className="border-t border-border/60">
                          <td className="py-1 pr-3 font-medium">{c.campo}</td>
                          <td className="py-1 pr-3 text-muted-foreground line-through">{auditVal(c.antes)}</td>
                          <td className="py-1">{auditVal(c.despues)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function CargasModal({ empleado, cerrar }: { empleado: EmpleadoRow; cerrar: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isFetching } = useQuery<CargasData>({
    queryKey: ['nom-cargas', empleado.id],
    queryFn: () => nomRpc<CargasData>('nom_cargas', { _empleado_id: empleado.id }),
  });
  const [form, setForm] = useState<typeof CARGA_VACIA>(CARGA_VACIA);
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-cargas', empleado.id] });
    void qc.invalidateQueries({ queryKey: ['nom-empleados'] });
  };
  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_carga_guardar', {
      _data: {
        empleado_id: empleado.id, id: form.id || null, parentesco: form.parentesco, nombre: form.nombre,
        identificacion: form.identificacion || null,
        fecha_nacimiento: form.parentesco === 'hijo' ? form.fecha_nacimiento : null,
        discapacidad: form.discapacidad, acreditada: form.acreditada, documento: form.documento || null,
        percibe_ingresos: form.percibe_ingresos,
      },
    }),
    onSuccess: () => { toast.success('Carga guardada'); setForm(CARGA_VACIA); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_carga_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Carga eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Cargas familiares — ${empleado.razon_social}`} onClose={cerrar} wide>
      <div className="space-y-4">
        {dlg.ui}
        <div className="text-xs text-muted-foreground">
          Utilidades (Art. 97 CT): cónyuge/conviviente + hijos menores de 18 o con discapacidad.
          Rebaja de IR (LORTI): además padres e hijos hasta 21 años, siempre que no perciban ingresos gravados.
          Solo cuentan las cargas acreditadas; con detalle registrado, ambos cálculos son automáticos por año fiscal.
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-full text-xs bg-estable-soft text-estable font-semibold">
            Utilidades {new Date().getFullYear()}: {data?.validas_utilidades ?? '—'}
          </span>
          <span className="px-2 py-1 rounded-full text-xs bg-estable-soft text-estable font-semibold">
            Rebaja IR: {data?.validas_ir ?? '—'}
          </span>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        </div>

        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">Parentesco</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Nacimiento</th>
            <th className="py-2 pr-3">Disc.</th><th className="py-2 pr-3">Documento</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {(data?.cargas ?? []).length === 0 && !isFetching && (
              <tr><td colSpan={7} className="py-5 text-center text-muted-foreground">Sin cargas registradas (se usa el número manual de la ficha)</td></tr>
            )}
            {(data?.cargas ?? []).map((c) => {
              return (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{PARENTESCOS[c.parentesco]}{c.percibe_ingresos && <div className="text-xs text-muted-foreground">percibe ingresos</div>}</td>
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3 font-mono">{c.fecha_nacimiento ? fecha(c.fecha_nacimiento) : '—'}</td>
                  <td className="py-2 pr-3">{c.discapacidad ? 'Sí' : '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{c.documento ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap space-x-1">
                    <ChipRegimen etiqueta="Util." califica={c.califica_utilidades} acreditada={c.acreditada} />
                    <ChipRegimen etiqueta="IR" califica={c.califica_ir} acreditada={c.acreditada} />
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar"
                      onClick={() => setForm({ id: c.id, parentesco: c.parentesco, nombre: c.nombre, identificacion: c.identificacion ?? '', fecha_nacimiento: c.fecha_nacimiento?.slice(0, 10) ?? '', discapacidad: c.discapacidad, acreditada: c.acreditada, documento: c.documento ?? '', percibe_ingresos: c.percibe_ingresos })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar a ${c.nombre}?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(c.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="border-t pt-3 space-y-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase">{form.id ? 'Editar carga' : 'Agregar carga'}</div>
          <div className="grid grid-cols-3 gap-3">
            <Campo label="Parentesco *">
              <select className={fieldCls} value={form.parentesco} onChange={(e) => setForm({ ...form, parentesco: e.target.value })}>
                {Object.entries(PARENTESCOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
            <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <Campo label="Identificación"><input className={fieldCls} value={form.identificacion} onChange={(e) => setForm({ ...form, identificacion: e.target.value })} /></Campo>
            {form.parentesco === 'hijo' && (
              <Campo label="Fecha de nacimiento *"><input type="date" className={fieldCls} value={form.fecha_nacimiento} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} /></Campo>
            )}
            <Campo label="Documento de respaldo"><input className={fieldCls} value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} placeholder="Partida, acta, carné CONADIS…" /></Campo>
          </div>
          <div className="flex flex-wrap gap-4">
            {form.parentesco === 'hijo' && <Switch checked={form.discapacidad} onChange={(v) => setForm({ ...form, discapacidad: v })} label="Con discapacidad (cuenta a cualquier edad)" />}
            <Switch checked={form.acreditada} onChange={(v) => setForm({ ...form, acreditada: v })} label="Acreditada (documento presentado)" />
            <Switch checked={form.percibe_ingresos} onChange={(v) => setForm({ ...form, percibe_ingresos: v })} label="Percibe ingresos gravados (no cuenta para IR)" />
          </div>
          <div className="flex justify-end gap-2">
            {form.id && <Button variant="outline" onClick={() => setForm(CARGA_VACIA)}>Cancelar edición</Button>}
            <Button isLoading={guardar.isPending}
              disabled={!form.nombre.trim() || (form.parentesco === 'hijo' && !form.fecha_nacimiento)}
              onClick={() => guardar.mutate()}>{form.id ? 'Guardar cambios' : 'Agregar'}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const vacio = {
  id: '', identificacion: '', tipo_identificacion: 'CEDULA', razon_social: '',
  email: '', celular: '', telefono: '', direccion: '', ciudad: '',
  fecha_nacimiento: '', genero: '', estado_civil: '', cargas_familiares: 0,
  discapacidad: false, discapacidad_pct: '',
  contacto_emergencia_nombre: '', contacto_emergencia_telefono: '',
  banco_nombre: '', banco_codigo: '', banco_tipo_cuenta: '', banco_cuenta: '', frecuencia_pago: 'mensual', es_activo: true,
  centro_costo_id: '',
};
type Form = typeof vacio;

export function Empleados() {
  const qc = useQueryClient();
  const [importar, setImportar] = useState(false);
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  const { data: rows = [], isFetching } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados', busqueda, soloActivos],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: busqueda || null, _solo_activos: soloActivos }),
  });
  const { data: centros = [] } = useQuery<CentroCostoOpt[]>({
    queryKey: ['cont-centros'],
    queryFn: centrosCostoList,
    staleTime: 60_000,
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<Form>(vacio);
  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [contratoVista, setContratoVista] = useState<ContratoRow | null>(null);

  const abrirNuevo = () => { setForm(vacio); setContratoVista(null); setAbierto(true); };
  const abrirEditar = async (row: EmpleadoRow) => {
    setCargandoFicha(true);
    try {
      const det = await nomRpc<{ empleado: EmpleadoFicha | null; contratos: ContratoRow[] }>('nom_empleado', { _id: row.id });
      const e = det.empleado;
      if (!e) { toast.error('Empleado no encontrado'); return; }
      setContratoVista(contratoVigente(det.contratos));
      setForm({
        id: e.id, identificacion: e.identificacion, tipo_identificacion: e.tipo_identificacion ?? 'CEDULA',
        razon_social: e.razon_social, email: e.email ?? '', celular: e.celular ?? '', telefono: e.telefono ?? '',
        direccion: e.direccion ?? '', ciudad: e.ciudad ?? '',
        fecha_nacimiento: e.fecha_nacimiento ?? '', genero: e.genero ?? '', estado_civil: e.estado_civil ?? '',
        cargas_familiares: e.cargas_familiares ?? 0,
        discapacidad: e.discapacidad, discapacidad_pct: e.discapacidad_pct != null ? String(e.discapacidad_pct) : '',
        contacto_emergencia_nombre: e.contacto_emergencia_nombre ?? '', contacto_emergencia_telefono: e.contacto_emergencia_telefono ?? '',
        banco_nombre: e.banco_nombre ?? '', banco_codigo: e.banco_codigo ?? '', banco_tipo_cuenta: e.banco_tipo_cuenta ?? '', banco_cuenta: e.banco_cuenta ?? '',
        frecuencia_pago: e.frecuencia_pago ?? 'mensual',
        es_activo: e.es_activo,
        centro_costo_id: e.centro_costo_id ?? '',
      });
      setAbierto(true);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setCargandoFicha(false); }
  };

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_empleado_guardar', {
      _data: {
        ...form,
        discapacidad_pct: form.discapacidad ? form.discapacidad_pct || null : null,
        centro_costo_id: form.centro_costo_id || null,
      },
    }),
    onSuccess: () => { toast.success('Empleado guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-empleados'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [cargasDe, setCargasDe] = useState<EmpleadoRow | null>(null);
  const [historialDe, setHistorialDe] = useState<EmpleadoRow | null>(null);
  const [accesoDe, setAccesoDe] = useState<EmpleadoRow | null>(null);
  const [accUsername, setAccUsername] = useState('');
  const [accEmail, setAccEmail] = useState('');
  const [accPassword, setAccPassword] = useState('');
  const crearAcceso = useMutation({
    mutationFn: () => nomRpc('nom_crear_acceso_empleado', {
      _empleado_id: accesoDe!.id, _username: accUsername.trim(), _email: accEmail.trim(), _password: accPassword,
    }),
    onSuccess: () => { toast.success(`Acceso al portal creado para ${accUsername}`); setAccesoDe(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [resetDe, setResetDe] = useState<EmpleadoRow | null>(null);
  const [claveTemporal, setClaveTemporal] = useState<{ nombre: string; clave: string } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const resetearAcceso = useMutation({
    mutationFn: () => nomRpc<{ clave_temporal: string }>('nom_resetear_clave_empleado', { _empleado_id: resetDe!.id }),
    onSuccess: (data) => { setClaveTemporal({ nombre: resetDe!.razon_social, clave: data.clave_temporal }); setResetDe(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const copiarClave = () => {
    if (!claveTemporal) return;
    void navigator.clipboard?.writeText(claveTemporal.clave);
    toast.success('Clave copiada');
    setCopiado(true);
  };

  const headersExp = ['N°', 'Código', 'Nombre', 'Cédula', 'Cargo', 'Departamento', 'Finca', 'Contrato', 'Jornada', 'Ingreso', 'Sueldo', 'Estado'];
  const MONEYCOLS = [10];
  const filasExport: Celda[][] = [
    ...rows.map((r, i): Celda[] => [
      i + 1, r.codigo, r.razon_social, r.identificacion, r.cargo ?? '—', r.departamento ?? '—', r.centro_costo ?? '—',
      r.tipo_contrato ? (TIPOS_CONTRATO[r.tipo_contrato] ?? r.tipo_contrato).split(' (')[0] : 'Sin contrato',
      jornadaLabel(r.jornada),
      fecha(r.fecha_inicio), r.sueldo != null ? Number(r.sueldo) : '', r.es_activo ? 'Activo' : 'Inactivo',
    ]),
    ['', '', '', '', '', '', '', '', '', 'TOTAL', rows.reduce((s, r) => s + (r.sueldo != null ? Number(r.sueldo) : 0), 0), ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <h1 className="text-xl font-semibold">{form.id ? 'Ficha del empleado' : 'Nuevo empleado'}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Empleados</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportar(true)}>
              <Upload className="w-4 h-4 mr-1" /> Cargar desde archivo
            </Button>
            <ExportarBotones
              nombre={`empleados-${fechaLocal()}`}
              titulo="Empleados" headers={headersExp} filas={filasExport} colsDerecha={[0, 9]}
              disabled={rows.length === 0}
              fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            />
            <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo empleado</Button>
          </div>
        </div>
      )}

      {!abierto && (<>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <input
            className={`${fieldCls} pl-8 w-72`} placeholder="Nombre, cédula o código…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setBusqueda(q.trim()); }}
          />
        </div>
        <Button variant="outline" onClick={() => setBusqueda(q.trim())}>Buscar</Button>
        <Switch checked={soloActivos} onChange={setSoloActivos} label="Solo activos" />
        {cargandoFicha && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Empleados ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Cédula</th>
              <th className="py-2 pr-3">Cargo</th><th className="py-2 pr-3">Departamento</th><th className="py-2 pr-3">Finca</th><th className="py-2 pr-3">Contrato</th>
              <th className="py-2 pr-3">Jornada</th>
              <th className="py-2 pr-3">Ingreso</th><th className="py-2 pr-3 text-right">Sueldo</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">Sin empleados</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                  <td className="py-2 pr-3">{r.razon_social}</td>
                  <td className="py-2 pr-3 font-mono">{r.identificacion}</td>
                  <td className="py-2 pr-3">{r.cargo ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.departamento ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.centro_costo ?? '—'}</td>
                  <td className="py-2 pr-3">{r.tipo_contrato ? (TIPOS_CONTRATO[r.tipo_contrato] ?? r.tipo_contrato).split(' (')[0] : 'Sin contrato'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{jornadaLabel(r.jornada)}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(r.fecha_inicio)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.sueldo)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.es_activo ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>
                      {r.es_activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar ficha" onClick={() => void abrirEditar(r)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Cargas familiares (Art. 97)"
                      onClick={() => setCargasDe(r)}><Users className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Historial de cambios"
                      onClick={() => setHistorialDe(r)}><History className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Crear acceso al portal"
                      onClick={() => { setAccesoDe(r); setAccUsername(''); setAccEmail(r.email ?? ''); setAccPassword(''); }}><KeyRound className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Resetear acceso al portal"
                      onClick={() => setResetDe(r)}><RefreshCw className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {cargasDe && <CargasModal empleado={cargasDe} cerrar={() => setCargasDe(null)} />}
      {historialDe && <HistorialModal empleado={historialDe} cerrar={() => setHistorialDe(null)} />}

      {accesoDe && (
        <Modal title={`Acceso al portal — ${accesoDe.razon_social}`} onClose={() => setAccesoDe(null)}>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Crea el login del Portal del Empleado (perfil «empleado»): solo verá sus roles de pago, vacaciones y solicitudes.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Usuario *"><input className={fieldCls} value={accUsername} onChange={(e) => setAccUsername(e.target.value)} /></Campo>
              <Campo label="Email *"><input className={fieldCls} value={accEmail} onChange={(e) => setAccEmail(e.target.value)} /></Campo>
            </div>
            <Campo label="Clave temporal * (mín. 8 caracteres)"><input type="password" className={fieldCls} value={accPassword} onChange={(e) => setAccPassword(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAccesoDe(null)}>Cancelar</Button>
              <Button isLoading={crearAcceso.isPending} disabled={!accUsername.trim() || !accEmail.trim() || accPassword.length < 8} onClick={() => crearAcceso.mutate()}>Crear acceso</Button>
            </div>
          </div>
        </Modal>
      )}

      {resetDe && (
        <Modal title={`Resetear acceso — ${resetDe.razon_social}`} onClose={() => setResetDe(null)}>
          <div className="space-y-3">
            <p className="text-sm">
              Vas a resetear el acceso de <b>{resetDe.razon_social}</b> (cédula <b>{resetDe.identificacion}</b>).
              Verifica su identidad antes de continuar.
            </p>
            <p className="text-xs text-muted-foreground">
              Se generará una clave temporal nueva para su acceso al Portal del Empleado; deberá cambiarla
              al ingresar.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResetDe(null)}>Cancelar</Button>
              <Button isLoading={resetearAcceso.isPending} onClick={() => resetearAcceso.mutate()}>Resetear acceso</Button>
            </div>
          </div>
        </Modal>
      )}

      {claveTemporal && (
        <Modal title={`Clave temporal — ${claveTemporal.nombre}`} onClose={() => { setClaveTemporal(null); setCopiado(false); }}>
          <div className="space-y-3">
            <div className="rounded-md border-2 border-primary bg-primary/5 p-4 text-center">
              <div className="text-xs text-muted-foreground uppercase mb-1">Clave temporal</div>
              <div className="text-2xl font-mono font-bold tracking-widest">{claveTemporal.clave}</div>
            </div>
            <Button variant="outline" className="w-full" onClick={copiarClave}>
              {copiado ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copiado ? 'Copiada' : 'Copiar'}
            </Button>
            <p className="text-sm text-muted-foreground">
              Entrégala al empleado. Deberá cambiarla al ingresar. No se volverá a mostrar.
            </p>
            <div className="flex justify-end pt-1">
              <Button onClick={() => { setClaveTemporal(null); setCopiado(false); }}>Entendido</Button>
            </div>
          </div>
        </Modal>
      )}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase">Datos personales</div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Identificación (cédula) *"><input className={fieldCls} value={form.identificacion} onChange={(e) => setForm({ ...form, identificacion: e.target.value })} disabled={!!form.id} /></Campo>
              <Campo label="Nombre completo *" className="col-span-2"><input className={fieldCls} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} placeholder="APELLIDOS NOMBRES" /></Campo>
              <Campo label="Fecha de nacimiento"><input type="date" className={fieldCls} value={form.fecha_nacimiento} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} /></Campo>
              <Campo label="Género">
                <select className={fieldCls} value={form.genero} onChange={(e) => setForm({ ...form, genero: e.target.value })}>
                  <option value="">—</option>{GENEROS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Campo>
              <Campo label="Estado civil">
                <select className={fieldCls} value={form.estado_civil} onChange={(e) => setForm({ ...form, estado_civil: e.target.value })}>
                  <option value="">—</option>{ESTADOS_CIVILES.map((g) => <option key={g} value={g}>{g.replace(/_/g, ' ')}</option>)}
                </select>
              </Campo>
              <Campo label="Email"><input className={fieldCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Campo>
              <Campo label="Celular"><input className={fieldCls} value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} /></Campo>
              <Campo label="Ciudad"><input className={fieldCls} value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></Campo>
              <Campo label="Dirección" className="col-span-3"><input className={fieldCls} value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></Campo>
            </div>

            <div className="text-xs font-semibold text-muted-foreground uppercase pt-1">Datos laborales</div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Cargas familiares (utilidades, Art. 97 CT)"><input type="number" min="0" className={fieldCls} value={form.cargas_familiares} onChange={(e) => setForm({ ...form, cargas_familiares: Number(e.target.value) })} /></Campo>
              <div className="pt-6"><Switch checked={form.discapacidad} onChange={(v) => setForm({ ...form, discapacidad: v })} label="Persona con discapacidad" /></div>
              {form.discapacidad && (
                <Campo label="% discapacidad"><input type="number" min="1" max="100" className={fieldCls} value={form.discapacidad_pct} onChange={(e) => setForm({ ...form, discapacidad_pct: e.target.value })} /></Campo>
              )}
              <Campo label="Contacto de emergencia"><input className={fieldCls} value={form.contacto_emergencia_nombre} onChange={(e) => setForm({ ...form, contacto_emergencia_nombre: e.target.value })} /></Campo>
              <Campo label="Teléfono de emergencia"><input className={fieldCls} value={form.contacto_emergencia_telefono} onChange={(e) => setForm({ ...form, contacto_emergencia_telefono: e.target.value })} /></Campo>
              <Campo label="Finca / Centro de costo">
                <select className={fieldCls} value={form.centro_costo_id} onChange={(e) => setForm({ ...form, centro_costo_id: e.target.value })}>
                  <option value="">— Sin asignar —</option>
                  {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
              {form.id && (
                <>
                  <Campo label="Jornada (del contrato vigente)">
                    <div className={`${fieldCls} bg-muted/50 text-muted-foreground cursor-not-allowed`}>
                      {contratoVista ? jornadaLabel(contratoVista.jornada) : 'Sin contrato'}
                    </div>
                  </Campo>
                  <Campo label="Tipo de contrato (del contrato vigente)">
                    <div className={`${fieldCls} bg-muted/50 text-muted-foreground cursor-not-allowed`}>
                      {contratoVista ? tipoContratoLabel(contratoVista.tipo) : 'Sin contrato'}
                    </div>
                  </Campo>
                </>
              )}
            </div>

            <div className="text-xs font-semibold text-muted-foreground uppercase pt-1">Pago (acreditación bancaria)</div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Banco">
                <select className={fieldCls} value={form.banco_nombre} onChange={(e) => setForm({ ...form, banco_nombre: e.target.value })}>
                  <option value="">—</option>{BANCOS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Campo>
              <Campo label="Tipo de cuenta">
                <select className={fieldCls} value={form.banco_tipo_cuenta} onChange={(e) => setForm({ ...form, banco_tipo_cuenta: e.target.value })}>
                  <option value="">—</option><option value="ahorros">Ahorros</option><option value="corriente">Corriente</option>
                </select>
              </Campo>
              <Campo label="N.º de cuenta"><input className={fieldCls} value={form.banco_cuenta} onChange={(e) => setForm({ ...form, banco_cuenta: e.target.value })} /></Campo>
              {/* Algunos layouts de banco piden el código de la institución, no su nombre. */}
              <Campo label="Código del banco (si el archivo lo pide)">
                <input className={fieldCls} value={form.banco_codigo} onChange={(e) => setForm({ ...form, banco_codigo: e.target.value })} />
              </Campo>
              <Campo label="Frecuencia de pago">
                <select className={fieldCls} value={form.frecuencia_pago} onChange={(e) => setForm({ ...form, frecuencia_pago: e.target.value })}>
                  {Object.entries(FRECUENCIAS_PAGO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Campo>
            </div>
            {form.frecuencia_pago !== 'mensual' && (
              <p className="text-xs text-muted-foreground">
                Los pagos {form.frecuencia_pago === 'quincenal' ? 'quincenales' : 'semanales'} se generan como anticipos sin
                descuentos (Movimientos → Anticipos → Generar corrida); el rol mensual consolida con los descuentos de ley
                y resta lo anticipado.
              </p>
            )}

            {form.id && <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Ficha activa" />}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.identificacion.trim() || !form.razon_social.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
          </CardContent>
        </Card>
      )}

      {importar && <ImportarPersonalModal onClose={() => setImportar(false)} />}
    </div>
  );
}
